import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { signOut } from "firebase/auth";
import { auth } from "hooks/useFirebase";
import { colors, constants } from "utils";
import { trackClickedButton, resetAnalytics } from "utils/analytics";
import { ReactComponent as SettingsIcon } from "assets/nav/settings.svg";
import { ReactComponent as LogOutIcon } from "assets/nav/log-out.svg";
import { ReactComponent as CloseIcon } from "assets/nav/close.svg";

// Figma "Profile info" (4421:96876): a hairline above, the user row, and the
// panel (4421:99858) that opens above the row, as wide as the row.
const Container = styled.div`
  position: relative;
  padding: 16px 16px 32px;
  border-top: 1px solid #acafad;
  flex-shrink: 0;
`;

// The row tints blue/50 on hover and stays tinted while the panel is open.
const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  background-color: ${(p) => (p.$open ? colors.blue50 : "transparent")};

  &:hover {
    background-color: ${colors.blue50};
  }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${colors.blue500};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.25px;
  overflow: hidden;
  flex-shrink: 0;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
  flex: 1;
  min-width: 0;
`;

const Truncated = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserName = styled(Truncated)`
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
`;

const UserEmail = styled(Truncated)`
  font-size: 14px;
  line-height: 20px;
  color: ${colors.milkGlass950};
`;

const ActiveSchool = styled(Truncated)`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${colors.milkGlass950};
`;

const GearButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  padding: 0;

  &:focus-visible {
    outline: 2px solid ${colors.blue500};
    outline-offset: 2px;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Panel = styled.div`
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: calc(100% - 2px);
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border-radius: 16px;
  background: ${colors.blue50};
  box-shadow: 0 16px 32px rgba(12, 12, 13, 0.1), 0 4px 4px rgba(12, 12, 13, 0.05);
  z-index: 10;
`;

const PanelRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const PanelItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px;
  border: none;
  border-radius: 6px;
  background: #fff;
  color: ${colors.nightlyWoods500};
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  cursor: pointer;
  text-align: left;

  &:hover,
  &:focus-visible {
    color: ${colors.blue700};
  }

  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }
`;

// The frame draws the X at 15px; the button around it is the hit target.
const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin: -4px -4px 0 0;
  padding: 0;
  border: none;
  background: transparent;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
  flex-shrink: 0;

  svg {
    width: 15px;
    height: 15px;
  }
`;

const getInitials = (name, email) => {
  if (name) {
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }
  return "??";
};

export const UserProfileCard = ({ user, activeSchool, onOpenSettings }) => {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const gearRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        gearRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const handleLogout = async () => {
    try {
      trackClickedButton("Logout", { page_name: "Global" });
      await signOut(auth);
      resetAnalytics();
      history.push(constants.EDUCATOR_LOGIN_PATH);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  if (!user) {
    return null;
  }

  const displayName = user.displayName || "";
  const email = user.email || "";
  const photoURL = user.photoURL;
  const close = () => setOpen(false);

  return (
    <Container ref={rootRef}>
      {open && (
        <Panel id="profile-menu" role="group" aria-label="Profile menu">
          <PanelRows>
            {onOpenSettings && (
              <PanelItem
                type="button"
                onClick={() => {
                  close();
                  onOpenSettings();
                }}
              >
                <SettingsIcon aria-hidden="true" focusable="false" />
                {constants.SETTINGS}
              </PanelItem>
            )}
            <PanelItem type="button" onClick={handleLogout}>
              <LogOutIcon aria-hidden="true" focusable="false" />
              Log Out
            </PanelItem>
          </PanelRows>
          <CloseButton type="button" aria-label="Close profile menu" onClick={close}>
            <CloseIcon aria-hidden="true" focusable="false" />
          </CloseButton>
        </Panel>
      )}
      <UserInfo $open={open}>
        <Avatar>
          {photoURL ? (
            <AvatarImage src={photoURL} alt={displayName || email} />
          ) : (
            getInitials(displayName, email)
          )}
        </Avatar>
        <UserDetails>
          {displayName && <UserName>{displayName}</UserName>}
          {email && <UserEmail>{email}</UserEmail>}
          {activeSchool?.name && (
            <ActiveSchool title={activeSchool.name}>
              {activeSchool.name}
            </ActiveSchool>
          )}
        </UserDetails>
        <GearButton
          type="button"
          ref={gearRef}
          aria-label="Profile menu"
          aria-expanded={open}
          aria-controls="profile-menu"
          onClick={() => setOpen((o) => !o)}
        >
          <SettingsIcon aria-hidden="true" focusable="false" />
        </GearButton>
      </UserInfo>
    </Container>
  );
};
